import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ShieldAlert,
  Swords,
  HeartHandshake,
  Users,
  ServerCog,
  AlertTriangle,
  Sparkles,
  Crown,
  Shield,
  Zap,
  Map as MapIcon,
  Search,
  X,
  ArrowUp,
  Printer,
  ArrowLeft,
  ChevronRight,
  Info,
} from "lucide-react";
import { useSiteContent, resolveImg } from "@/admin/SiteContent";

const ICON_MAP = {
  BookOpen, ShieldAlert, Swords, HeartHandshake, Users, ServerCog,
  AlertTriangle, Sparkles, Crown, Shield, Zap, Map: MapIcon,
};

const ROMAN = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV", "XV"];

// Highlight matching search text inline
const Highlighted = ({ text = "", query = "" }) => {
  if (!query || query.length < 2) return <>{text}</>;
  const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  return (
    <>
      {text.split(re).map((part, i) =>
        re.test(part) ? (
          <mark
            key={i}
            className="bg-[#D4AF37]/25 text-[#F5F5F5] rounded px-0.5"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

/* --- Section renderers per type --- */
const SectionBody = ({ text, query }) => {
  const paragraphs = (text || "").split(/\n\n+/).filter(Boolean);
  return (
    <div className="space-y-4">
      {paragraphs.map((p, i) => (
        <p key={i} className="text-[#C9C9D2] text-[15px] leading-[1.85]">
          <Highlighted text={p} query={query} />
        </p>
      ))}
    </div>
  );
};

const SectionList = ({ items, query }) => (
  <ul className="space-y-2.5">
    {(items || []).map((it, i) => (
      <li
        key={i}
        className="flex gap-3 text-[#C9C9D2] text-[15px] leading-[1.75]"
      >
        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#D4AF37]" />
        <span><Highlighted text={it} query={query} /></span>
      </li>
    ))}
  </ul>
);

const SectionDefs = ({ defs, query }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {(defs || []).map((d, i) => (
      <div
        key={i}
        className="rounded-xl border border-white/[0.06] bg-[#0B0B0F]/60 p-4 hover:border-[#D4AF37]/40 transition-colors"
      >
        <div className="font-mono-pl text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] mb-1.5">
          <Highlighted text={d.t} query={query} />
        </div>
        <p className="text-[#C9C9D2] text-[13.5px] leading-[1.7]">
          <Highlighted text={d.d} query={query} />
        </p>
      </div>
    ))}
  </div>
);

const SectionTable = ({ table, query }) => (
  <div className="overflow-x-auto rounded-xl border border-white/[0.08] bg-[#0B0B0F]/60">
    <table className="w-full text-left text-sm">
      <thead className="bg-[#D4AF37]/[0.06] border-b border-[#D4AF37]/20">
        <tr>
          {(table?.head || []).map((h, i) => (
            <th
              key={i}
              className="px-4 py-3 font-mono-pl text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(table?.rows || []).map((row, r) => (
          <tr key={r} className="border-t border-white/[0.06]">
            {row.map((cell, c) => (
              <td key={c} className="px-4 py-3 align-top text-[#C9C9D2]">
                <Highlighted text={cell} query={query} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SectionRobberies = ({ robberies, query }) => (
  <div className="grid sm:grid-cols-2 gap-5 mt-2">
    {(robberies || []).map((rb, i) => (
      <article
        key={i}
        className="rounded-2xl p-6 border border-[#E26B6B]/25 bg-gradient-to-br from-[#E26B6B]/[0.06] to-transparent hover:border-[#E26B6B]/50 transition-colors"
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h5 className="font-display text-xl sm:text-2xl uppercase text-[#F5F5F5] tracking-wide">
              <Highlighted text={rb.name} query={query} />
            </h5>
            {rb.sub && (
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE] mt-1">
                <Highlighted text={rb.sub} query={query} />
              </p>
            )}
          </div>
          <span className="font-mono-pl text-[11px] text-[#E26B6B] px-2.5 py-1 rounded-full border border-[#E26B6B]/30 bg-[#E26B6B]/5">
            {String(i + 1).padStart(2, "0")}
          </span>
        </div>
        {rb.stats && (
          <div className="grid grid-cols-3 gap-2 mb-5">
            {[
              { k: "Rapinatori", v: rb.stats.rapinatori, c: "text-[#E26B6B] border-[#E26B6B]/30" },
              { k: "Poliziotti", v: rb.stats.poliziotti, c: "text-[#00D4FF] border-[#00D4FF]/30" },
              { k: "Ostaggi", v: rb.stats.ostaggi, c: "text-[#D4AF37] border-[#D4AF37]/35" },
            ].map((st) => (
              <div
                key={st.k}
                className={`rounded-xl border ${st.c} bg-[#0B0B0F]/60 px-3 py-3 text-center`}
              >
                <div className="text-[9px] uppercase tracking-[0.22em] text-[#B6B6BE] mb-1">
                  {st.k}
                </div>
                <div className="font-display text-base sm:text-lg text-[#F5F5F5]">
                  {st.v || "—"}
                </div>
              </div>
            ))}
          </div>
        )}
        {rb.rules && (
          <ul className="space-y-2 pt-4 border-t border-white/[0.08]">
            {rb.rules.map((r, j) => (
              <li key={j} className="flex items-start gap-2.5 text-sm text-[#C9C9D2] leading-relaxed">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-[#D4AF37] shrink-0" />
                <span><Highlighted text={r} query={query} /></span>
              </li>
            ))}
          </ul>
        )}
      </article>
    ))}
  </div>
);

const Section = ({ section, query }) => {
  return (
    <div className="mt-6">
      {section.h && (
        <h3 className="font-display text-xl sm:text-2xl text-[#F5F5F5] mb-3 tracking-wide">
          <Highlighted text={section.h} query={query} />
        </h3>
      )}
      {section.body && <SectionBody text={section.body} query={query} />}
      {section.list && <SectionList items={section.list} query={query} />}
      {section.defs && <SectionDefs defs={section.defs} query={query} />}
      {section.table && <SectionTable table={section.table} query={query} />}
      {section.robberies && <SectionRobberies robberies={section.robberies} query={query} />}
    </div>
  );
};

/* Determines if a chapter/section matches the current search query */
const chapterMatchesQuery = (ch, q) => {
  if (!q || q.length < 2) return true;
  const nq = q.toLowerCase();
  const inStr = (s) => (s || "").toLowerCase().includes(nq);
  if (inStr(ch.title) || inStr(ch.subtitle) || inStr(ch.intro)) return true;
  return (ch.sections || []).some((s) => {
    if (inStr(s.h) || inStr(s.body)) return true;
    if ((s.list || []).some(inStr)) return true;
    if ((s.defs || []).some((d) => inStr(d.t) || inStr(d.d))) return true;
    if ((s.table?.rows || []).some((r) => r.some(inStr))) return true;
    if ((s.robberies || []).some((h) => inStr(h.name) || inStr(h.sub) || (h.rules || []).some(inStr))) return true;
    return false;
  });
};

export default function RulesPage() {
  const { content } = useSiteContent();
  const [query, setQuery] = useState("");
  const [activeKey, setActiveKey] = useState("");
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);
  const chapterRefs = useRef({});
  const searchRef = useRef(null);

  const meta = content?.rules_meta || {};
  const hero = meta.hero || {};
  const toolbar = meta.toolbar || {};
  const footer = meta.footer || {};
  const staffNote = meta.staff_note || {};

  const chapters = useMemo(() => {
    const list = content?.rules || [];
    return list.filter((c) => chapterMatchesQuery(c, query));
  }, [content, query]);

  const chaptersAll = content?.rules || [];

  const updatedAt = content?.updated_at || "";
  const updatedLabel = useMemo(() => {
    if (!updatedAt) return "";
    try {
      return new Date(updatedAt).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch { return ""; }
  }, [updatedAt]);

  // Scroll spy + reading progress + back-to-top toggle
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = doc.scrollTop;
      const max = doc.scrollHeight - doc.clientHeight;
      const p = max > 0 ? Math.min(100, (scrolled / max) * 100) : 0;
      setProgress(p);
      setShowTop(scrolled > 600);

      // Active chapter detection
      let currentKey = "";
      Object.entries(chapterRefs.current).forEach(([key, el]) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < 220) currentKey = key;
      });
      if (currentKey) setActiveKey(currentKey);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [chapters]);

  // Keyboard shortcuts: "/" focus search, Esc clear
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") setQuery("");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const scrollToChapter = (key) => {
    const el = chapterRefs.current[key];
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const scrollTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-[#F5F5F5] rules-page">
      {/* Reading progress bar (top) */}
      <div
        className="fixed top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent z-50 print:hidden origin-left"
        style={{ transform: `scaleX(${progress / 100})`, transformOrigin: "0 0" }}
        data-testid="rules-progress-bar"
      />

      {/* ============ HERO ============ */}
      <section
        className="relative overflow-hidden border-b border-[#D4AF37]/15"
        data-testid="rules-hero"
      >
        {/* Bg image (optional) */}
        {hero.image_url && (
          <img
            src={resolveImg(hero.image_url)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-[0.18]"
            loading="lazy"
          />
        )}
        {/* Radial glow */}
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_-20%,rgba(212,175,55,0.18),transparent_60%)]" />
        {/* Marquee background text */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none select-none opacity-[0.04]">
          <div className="marquee">
            <div className="marquee__track font-gaming text-[12vw] leading-none text-[#D4AF37] whitespace-nowrap">
              <span>CODICE · CODICE · CODICE · CODICE ·</span>
              <span>CODICE · CODICE · CODICE · CODICE ·</span>
            </div>
          </div>
        </div>

        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-16 sm:pt-24 pb-12 sm:pb-16">
          {/* Breadcrumb / Back link */}
          <div className="flex items-center gap-3 mb-8 print:hidden">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.26em] text-[#B6B6BE] hover:text-[#D4AF37] transition-colors"
              data-testid="rules-back-home"
            >
              <ArrowLeft size={13} /> Torna alla Home
            </Link>
            {hero.badge && (
              <span className="ml-auto text-[10px] tracking-[0.28em] uppercase px-3 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] bg-[#0B0B0F]/40">
                {hero.badge}
              </span>
            )}
          </div>

          {hero.kicker && (
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#D4AF37]/60" />
              <span className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                {hero.kicker}
              </span>
            </div>
          )}
          <h1
            className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.95] tracking-wide mb-6"
            data-testid="rules-hero-title"
          >
            {hero.title_lead || "Il Regolamento"}{" "}
            <span className="text-gold glow-gold">{hero.title_accent || "di PureLife"}</span>
          </h1>
          {hero.subtitle && (
            <p className="max-w-2xl text-[#B6B6BE] text-base sm:text-lg leading-relaxed mb-8">
              {hero.subtitle}
            </p>
          )}

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center print:hidden">
            <div className="relative flex-1 max-w-xl">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B6B6BE]"
              />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={toolbar.search_placeholder || "Cerca…"}
                className="w-full pl-11 pr-10 py-3 rounded-full bg-[#0B0B0F]/70 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 focus:outline-none text-sm text-[#F5F5F5] placeholder:text-[#6b6b76]"
                data-testid="rules-search-input"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/5"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
              <kbd className="hidden sm:inline absolute -right-14 top-1/2 -translate-y-1/2 text-[10px] text-[#6b6b76] font-mono border border-white/10 rounded px-1.5 py-0.5">
                /
              </kbd>
            </div>
            <button
              onClick={() => window.print()}
              className="btn-ghost-neon inline-flex items-center gap-2 rounded-full px-5 py-3 text-[11px] uppercase tracking-[0.22em] whitespace-nowrap"
              data-testid="rules-print-btn"
            >
              <Printer size={13} /> {toolbar.print_label || "Stampa · PDF"}
            </button>
          </div>

          {updatedLabel && (
            <div className="mt-6 text-[11px] tracking-[0.22em] uppercase text-[#6b6b76]">
              Ultima modifica · {updatedLabel}
            </div>
          )}
        </div>
      </section>

      {/* ============ STAFF NOTE (optional) ============ */}
      {staffNote.enabled && staffNote.body && (
        <div
          className="mx-auto max-w-6xl px-5 sm:px-8 mt-8"
          data-testid="rules-staff-note"
        >
          <div className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/[0.08] to-transparent p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-2">
              <Info size={14} className="text-[#D4AF37]" />
              <span className="font-mono-pl text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
                {staffNote.title || "Nota dello Staff"}
              </span>
            </div>
            <p className="text-[#F5F5F5]/90 text-[15px] leading-[1.75] whitespace-pre-wrap">
              {staffNote.body}
            </p>
          </div>
        </div>
      )}

      {/* ============ MAIN — sidebar + content ============ */}
      <div className="mx-auto max-w-6xl px-5 sm:px-8 mt-14 grid lg:grid-cols-[240px_1fr] gap-10 pb-24">
        {/* Sidebar TOC */}
        <aside
          className="hidden lg:block sticky top-24 self-start print:hidden"
          data-testid="rules-toc"
        >
          <div className="font-mono-pl text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
            Indice del Codice
          </div>
          <nav className="space-y-1.5">
            {chaptersAll.map((ch, i) => {
              const Icon = ICON_MAP[ch.icon] || BookOpen;
              const isActive = activeKey === ch.key;
              const isMatch = chapterMatchesQuery(ch, query);
              return (
                <button
                  key={ch.key}
                  onClick={() => scrollToChapter(ch.key)}
                  className={`w-full text-left group flex items-center gap-3 rounded-lg px-3 py-2.5 border transition-all duration-200 ${
                    isActive
                      ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.06]"
                      : "border-transparent hover:border-white/10 hover:bg-white/[0.02]"
                  } ${!isMatch ? "opacity-30" : ""}`}
                  data-testid={`rules-toc-${ch.key}`}
                >
                  <span className={`font-mono-pl text-[10px] w-4 shrink-0 ${isActive ? "text-[#D4AF37]" : "text-[#6b6b76]"}`}>
                    {ROMAN[i + 1]}
                  </span>
                  <Icon
                    size={13}
                    className={`shrink-0 ${isActive ? "text-[#D4AF37]" : "text-[#B6B6BE]"}`}
                    strokeWidth={1.6}
                  />
                  <span
                    className={`text-[12.5px] leading-tight line-clamp-2 ${isActive ? "text-[#F5F5F5]" : "text-[#B6B6BE] group-hover:text-[#F5F5F5]"}`}
                  >
                    {ch.title}
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Chapters content */}
        <main className="min-w-0">
          {chapters.length === 0 && query && (
            <div className="text-center py-16 text-[#B6B6BE]" data-testid="rules-no-results">
              <div className="text-[#D4AF37] font-mono-pl text-[10px] tracking-[0.28em] uppercase mb-2">
                Nessun risultato
              </div>
              <p>Nessun articolo corrisponde a "{query}".</p>
            </div>
          )}

          {chapters.map((ch, i) => {
            const idx = chaptersAll.findIndex((c) => c.key === ch.key) + 1;
            const Icon = ICON_MAP[ch.icon] || BookOpen;
            return (
              <article
                key={ch.key}
                ref={(el) => (chapterRefs.current[ch.key] = el)}
                id={`rules-${ch.key}`}
                className={i > 0 ? "mt-20 pt-16 border-t border-white/[0.05]" : ""}
                data-testid={`rules-chapter-${ch.key}`}
              >
                {/* Chapter header — big roman numeral + icon */}
                <div className="flex items-start gap-5 sm:gap-7 mb-8">
                  <div className="shrink-0 select-none">
                    <div className="font-display text-6xl sm:text-8xl leading-none text-[#D4AF37]/25">
                      {ROMAN[idx]}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 pt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={14} className="text-[#D4AF37]" strokeWidth={1.6} />
                      <span className="font-mono-pl text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
                        {ch.subtitle || `Capitolo ${ROMAN[idx]}`}
                      </span>
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl uppercase text-[#F5F5F5] tracking-wide leading-tight">
                      <Highlighted text={ch.title} query={query} />
                    </h2>
                    {ch.intro && (
                      <p className="mt-4 text-[#B6B6BE] text-base leading-relaxed max-w-3xl">
                        <Highlighted text={ch.intro} query={query} />
                      </p>
                    )}
                  </div>
                </div>

                {/* Sections */}
                <div className="space-y-8 pl-0 sm:pl-[95px]">
                  {(ch.sections || []).map((s, k) => (
                    <Section key={k} section={s} query={query} />
                  ))}
                </div>
              </article>
            );
          })}
        </main>
      </div>

      {/* ============ FOOTER ============ */}
      <footer className="border-t border-[#D4AF37]/15 py-16 print:hidden">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          {footer.quote && (
            <p className="font-display text-2xl sm:text-3xl text-[#F5F5F5] italic mb-3">
              {footer.quote}
            </p>
          )}
          {footer.signature && (
            <p className="font-mono-pl text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
              {footer.signature}
            </p>
          )}
          <Link
            to="/"
            className="mt-8 inline-flex items-center gap-2 btn-ghost-neon rounded-full px-6 py-3 text-[11px] uppercase tracking-[0.22em]"
            data-testid="rules-footer-back"
          >
            <ArrowLeft size={13} /> {footer.cta_label || "Torna alla Home"}
          </Link>
        </div>
      </footer>

      {/* Back to top FAB */}
      {showTop && (
        <button
          onClick={scrollTop}
          className="fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full bg-[#D4AF37] text-[#0B0B0F] shadow-[0_10px_30px_rgba(212,175,55,0.4)] hover:scale-105 transition-transform flex items-center justify-center print:hidden"
          data-testid="rules-back-top"
          aria-label={toolbar.back_top_label || "Torna in cima"}
        >
          <ArrowUp size={18} strokeWidth={2.2} />
        </button>
      )}
    </div>
  );
}
